//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';

import { VALIDATION_STATUS } from '@apis/catalog-validation/catalogValidationModel';
import { ConnectPanelActions } from '@catalog-ingestion/actions/connectPanelActions';
import { GoogleCatalogIngestionModalActions } from '@catalog-ingestion/actions/googleCatalogIngestionModalActions';
import { ValidationErrorLayout } from '@catalog-ingestion/components/validation-panel/ValidationErrorLayout';
import { ICuralateStore } from '@models/AppState';

import { ReduxDispatch } from 'app/curalate.store';

function mapStateToProps(state: ICuralateStore) {
  return {
    validationStatus: state.catalogIngestionReducers.googleCatalogIngestionModal.validationStatus,
  };
}

const mapDispatchToProps = (dispatch: ReduxDispatch) => {
  return {
    actions: {
      modal: bindActionCreators(GoogleCatalogIngestionModalActions, dispatch),
      connectPanel: bindActionCreators(ConnectPanelActions, dispatch),
    },
  };
};

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;
export const ValidationPanelErrorContent = (props: Props) => {
  const {
    validationStatus,
    actions: {
      modal: { resetGoogleCatalogIngestionState, openGoogleCatalogIngestionModal, receiveValidationStatus },
      connectPanel: { toggleIsValidating },
    }
  } = props;

  const onRetry = () => {
    toggleIsValidating();
    resetGoogleCatalogIngestionState();
    receiveValidationStatus(validationStatus);
    openGoogleCatalogIngestionModal();
  };

  const getValidationContent = () => {
    switch (validationStatus) {
      case VALIDATION_STATUS.FAILED_DOWNLOAD:
        return (
          <ValidationErrorLayout
            errorMessage={ 'Nuts! We\'re having trouble downloading your Catalog URL.' }
            errorDescription='Please check your catalog URL for errors and try again'
            buttonText='Retry Catalog Connection'
            onButtonClick={ onRetry }
          />
        );
      case VALIDATION_STATUS.FAILED_FILE_PARSE:
        return (
          <ValidationErrorLayout
            errorMessage={ 'Nuts! We can\'t read your file format.' }
            hrefText='Learn more about accepted file formats'
            hrefLink='https://developers.curalate.com/guide/product-catalogs/#supported-schemas-and-file-formats'
            buttonText='Retry Catalog Connection'
            onButtonClick={ onRetry }
          />
        );
      case VALIDATION_STATUS.FAILED_UNKNOWN || VALIDATION_STATUS.FAILED_DOWNLOAD_TIMEOUT:
        return (
          <ValidationErrorLayout
            errorMessage={ 'Nuts! We couldn\'t process your product catalog.' }
            errorDescription='Please try again'
            buttonText='Retry Catalog Connection'
            onButtonClick={ onRetry }
          />
        );
    }
  };

  return (
    <PanelContainer>
      { getValidationContent() }
    </PanelContainer>
  );
};

const PanelContainer = styled.div`
  display: flex;
  justify-content: center;
`;

export default connect(mapStateToProps, mapDispatchToProps)(ValidationPanelErrorContent);
